/* ============================================================================
   Abasto. De dónde puede salir el alimento.

   Las demás pantallas miden precios. Ésta contesta otra cosa: si un banco quiere
   comprar frijol al mayoreo, ¿dónde está la oferta? Sale del DENUE del INEGI,
   que es un censo de establecimientos, no una encuesta.

   Dos límites que gobiernan el diseño y que la vista dice en voz alta:

   · El DENUE cuenta ESTABLECIMIENTOS, no capacidad. No publica ventas ni
     volumen. El corte «de 11 personas en adelante» separa la bodega familiar del
     proveedor que plausiblemente surte con factura, pero no garantiza nada, y
     por eso nunca se rotula como «proveedores capaces».

   · Los contactos NO se publican. El DENUE trae correo y teléfono, y la mayoría
     de los correos son de una persona y no de un buzón de empresa. Publicarlos
     en un sitio estático los vuelve raspables de un tirón. La lista existe, se
     genera en cada ingesta y vive fuera del sitio.
   ========================================================================== */

import { useMemo, useState } from 'react'
import { BarList, type BarRow } from '../charts/BarList'
import { Callout, Card, FilterBar, FilterField, Section, Select } from '../components/ui'
import { dataset } from '../data/dataset'
import {
  ALL_CLASSES,
  wholesaleByHub,
  wholesaleByState,
  wholesaleTotal,
} from '../data/derive'
import { integer, pluralize } from '../lib/format'
import { AbastoDirectorio } from './AbastoDirectorio'

export function Abasto() {
  const directory = dataset.wholesale
  const [clase, setClase] = useState(ALL_CLASSES)

  const claseOptions = useMemo(() => {
    if (!directory) return []
    return [
      { value: ALL_CLASSES, label: 'Todas las clases' },
      ...directory.classes
        .map((c) => ({ value: c.code, label: c.short }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es-MX')),
    ]
  }, [directory])

  const estados = useMemo(
    () => (directory ? wholesaleByState(directory, clase) : []),
    [directory, clase],
  )
  const plazas = useMemo(
    () => (directory ? wholesaleByHub(directory, clase) : []),
    [directory, clase],
  )
  const total = directory ? wholesaleTotal(directory, clase) : 0

  if (!directory) {
    return (
      <>
        <header className="view-head">
          <p className="t-micro view-head__eyebrow">Abasto</p>
          <h1 className="t-title1">De dónde puede salir el alimento</h1>
        </header>
        <Callout kind="warning" title="El directorio no se pudo ingerir">
          <p>
            La última corrida del pipeline no logró leer el DENUE, así que esta pantalla no tiene
            qué mostrar. El resto del sitio no depende de esta fuente.
          </p>
        </Callout>
      </>
    )
  }

  const claseNombre =
    clase === ALL_CLASSES
      ? null
      : (directory.classes.find((c) => c.code === clase)?.short.toLowerCase() ?? null)

  const filtrado = clase !== ALL_CLASSES

  /* «05_2026» → «mayo de 2026». Si el archivo no la trae, se cae a la fecha de
     modificación y, en último caso, se dice que no está en vez de inventarla. */
  const edicion = (() => {
    const m = /^(\d{2})_(\d{4})$/.exec(directory.edition)
    if (m) {
      const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre']
      const mes = meses[Number(m[1]) - 1]
      if (mes) return `${mes} de ${m[2]}`
    }
    return directory.editionDate || 'sin declarar'
  })()

  const filasEstado = useMemo<BarRow[]>(
    () =>
      estados.map((r) => ({
        id: r.id,
        label: r.name,
        value: r.total,
        magnitude: r.total,
      })),
    [estados],
  )

  const filasPlaza = useMemo<BarRow[]>(
    () =>
      plazas.map((r) => ({
        id: r.id,
        label: r.name,
        sublabel: r.sublabel,
        value: r.total,
        magnitude: r.total,
      })),
    [plazas],
  )

  return (
    <>
      <header className="view-head">
        <p className="t-micro view-head__eyebrow">Abasto</p>
        <h1 className="t-title1">De dónde puede salir el alimento</h1>
        <p className="t-body ink-2">
          Las demás pantallas miden cuánto cuesta la comida. Ésta contesta dónde comprarla al
          mayoreo. El INEGI publica el directorio de todas las unidades económicas del país; aquí
          están las {integer(directory.total)} que venden alimento de canasta básica al por mayor,
          en {integer(directory.municipalities)} municipios.
        </p>
      </header>

      <FilterBar ariaLabel="Filtros del directorio de abasto">
        <FilterField label="Qué venden">
          <Select
            value={clase}
            onChange={setClase}
            options={claseOptions}
            ariaLabel="Clase de producto"
          />
        </FilterField>
      </FilterBar>

      <div className="grid grid--kpi">
        <Card>
          <p className="t-micro ink-3">
            {filtrado ? `Mayoristas de ${claseNombre}` : 'Mayoristas de alimento'}
          </p>
          <p className="t-title2 tabular">{integer(total)}</p>
          <p className="t-caption ink-3">
            En {integer(estados.length)}{' '}
            {pluralize(estados.length, 'entidad', 'entidades')} del país.
          </p>
        </Card>
        <Card>
          <p className="t-micro ink-3">De 11 personas en adelante</p>
          <p className="t-title2 tabular">
            {filtrado ? '—' : integer(directory.volumeTotal)}
          </p>
          <p className="t-caption ink-3">
            {filtrado
              ? 'El estrato de empleados sólo está disponible sobre el total, no por clase.'
              : 'Los que plausiblemente surten volumen con factura. No es una medida de capacidad.'}
          </p>
        </Card>
        <Card>
          <p className="t-micro ink-3">Dentro de una central de abasto</p>
          <p className="t-title2 tabular">
            {filtrado ? '—' : integer(directory.inCentralTotal)}
          </p>
          <p className="t-caption ink-3">
            Repartidos en {integer(directory.hubs.length)}{' '}
            {pluralize(directory.hubs.length, 'plaza', 'plazas')}.
          </p>
        </Card>
        <Card>
          <p className="t-micro ink-3">Edición del directorio</p>
          <p className="t-title2">{edicion}</p>
          <p className="t-caption ink-3">
            La que declara el propio archivo del INEGI, no la fecha en que se descargó. El DENUE se
            actualiza dos veces al año.
          </p>
        </Card>
      </div>

      <Section
        title={
          filtrado
            ? `Dónde están los mayoristas de ${claseNombre}`
            : 'Dónde están los mayoristas de alimento'
        }
        description="Número de establecimientos por entidad. La barra mide cuántos hay, no cuánto pueden surtir."
      >
        <Card>
          <BarList
            rows={filasEstado}
            title={`${filasEstado.length} ${pluralize(filasEstado.length, 'entidad', 'entidades')}`}
            subtitle={
              filtrado
                ? `Establecimientos con la clase SCIAN de ${claseNombre}.`
                : 'Las doce clases de comercio al por mayor de alimento de canasta básica.'
            }
            nameHeading="Entidad"
            caption="Número de mayoristas de alimento por entidad federativa."
            valueHeading="Mayoristas"
            magnitudeHeading="Comparación"
            formatValue={(v) => integer(v)}
            mode="magnitude"
            magnitudeKind="currency"
            formatMagnitude={(v) => integer(v)}
            footnote="Fuente: INEGI, Directorio Estadístico Nacional de Unidades Económicas (DENUE), sector 43."
          />
        </Card>
      </Section>

      <Section
        title="Plazas de abasto"
        description="Municipios con una central de abasto, por número de bodegas de alimento dentro de ella."
      >
        <Card>
          <BarList
            rows={filasPlaza}
            title={`${filasPlaza.length} ${pluralize(filasPlaza.length, 'plaza', 'plazas')}`}
            subtitle="Se agrupan por municipio porque el DENUE registra la misma central con nombres distintos: en Toluca aparece como «Central de Abasto Toluca» y como «Central de Abastos»."
            nameHeading="Municipio"
            caption="Número de mayoristas de alimento dentro de centrales de abasto, por municipio."
            valueHeading="Bodegas"
            magnitudeHeading="Comparación"
            formatValue={(v) => integer(v)}
            mode="magnitude"
            magnitudeKind="currency"
            formatMagnitude={(v) => integer(v)}
            emptyMessage="Ninguna central de abasto registra mayoristas de esta clase."
            footnote="Fuente: INEGI, DENUE. Sólo establecimientos marcados dentro de una central de abasto."
          />
        </Card>
      </Section>

      <AbastoDirectorio claseActiva={clase} estados={directory.states} />

      <Callout kind="warning" title="Qué NO dice este directorio">
        <p>
          El DENUE cuenta <strong>establecimientos, no capacidad</strong>. No publica ventas, ni
          volumen, ni si alguien factura. El corte de once personas separa la bodega familiar del
          proveedor que plausiblemente surte una compra grande, pero no lo garantiza: confirmarlo es
          trabajo de campo, no de directorio.
        </p>
        <p>
          Tampoco dice precios. Para eso están las otras pantallas, y son fuentes distintas: el
          INEGI levanta precios en una muestra de ciudades, no en estos establecimientos.
        </p>
      </Callout>

      <Callout kind="info" title="Por qué aquí no hay teléfonos ni correos">
        <p>
          El DENUE incluye el contacto de {integer(directory.withContact)} de estos establecimientos,
          y sería fácil publicarlo. No se hace: la mayoría de esos correos son de una persona, no de
          un buzón de empresa, y ponerlos en una página pública los vuelve una lista raspable de un
          tirón, que es una cosa muy distinta de la consulta uno por uno que ofrece el INEGI.
        </p>
        <p>
          La lista con contactos sí se genera en cada ingesta y vive fuera de este sitio, para que
          quien la use lo haga con su propio proceso de consentimiento y pueda responder por él.
        </p>
      </Callout>
    </>
  )
}
